import styles from './PunchupDashboard.module.css';
import { ZERO_TIME } from 'utils/constants';
import { useEffect, useRef } from 'react';

import { CustomFieldsSection } from 'features/Dashboard/components/punchup/CustomFieldsSection';
import { GeneralSection } from 'features/Dashboard/components/punchup/GeneralSection';
import { ImgContentSection } from 'features/Dashboard/components/punchup/ImgContentSection';
import { MainLayout } from 'layouts/MainLayout';
import { AddOnsSection, useApplicableAddonsApi } from 'features/Dashboard/components/punchup/AddOnsSection';
import { PoolsSection } from 'features/Dashboard/components/punchup/PoolsSection';
import {
	PunchupAccordionGroup,
	PunchupAccordionSection,
	PunchupAccordionControls,
	usePunchupAccordion,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { PunchupSplitRow } from 'features/common/components/Punchup/primitives/layout/PunchupSplitRow';
import { EventActionButtons } from 'features/Dashboard/components/punchup/EventActionButtons';
import { EventInfoHeader } from 'features/Dashboard/components/punchup/EventInfoHeader';
import { EventHighlights } from 'features/Dashboard/components/punchup/EventHighlights';
import { TicketTypesSection } from 'features/Dashboard/components/punchup/TicketTypesSection';
import { PunchupSeatsSection } from 'features/Dashboard/components/punchup/SeatsSection';
import { camelize, convertUTCToDate } from 'utils/utils';
import moment from 'moment';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { Prompt } from 'react-router-dom';
import { useGetCustomFields } from 'features/common/components/CustomFieldsComponent/useGetCustomFields';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getEventPools } from 'features/PunchupPages/TicketDetails/services';
import { Pool } from 'features/PunchupPages/TicketDetails/types';
import { IdParamsType } from 'utils/types';
import { useTranslation } from 'react-i18next';
import { postToast } from 'features/common/utils/postFlash';

// Accordion order + default-open rationale: see ./CLAUDE.md. Order follows the setup sequence a manager
// works through (PUN-4942): show details → image → tiers → capacity/seating → extras → per-guest questions.
// This deliberately retires the old pools-before-tickets nudge (docs/pools_business_logic.md T-1).
const ACCORDION_SECTION_IDS = ['general', 'image', 'tickets', 'seats', 'pools', 'addons', 'customfields'] as const;

// Open on load (PUN-4942): the two sections a manager almost always came here for. Seeded into the
// hook's initial state — not opened from an effect — so they are open at first paint, before the host
// reveals the iframe. See ./CLAUDE.md → "Default-open sections".
const DEFAULT_OPEN_SECTION_IDS = ['tickets', 'customfields'] as const;

const HEADER_DATE_FORMAT = 'ddd, MMM D, YYYY';
const HEADER_TIME_FORMAT = 'h:mm A';

const PunchupDashboard = () => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();

	const { event, isError: isEventError } = useGetEvent();

	// Pools + custom-fields counts reuse the child sections' React Query keys (deduped); count-badge
	// failures are non-fatal (badge degrades, body still renders). See ./CLAUDE.md.
	const { data: pools = [] } = useQuery<Pool[]>({
		queryKey: ['eventPools', eventId],
		queryFn: (): Promise<Pool[]> => getEventPools(Number(eventId)),
		enabled: !!eventId,
		select: (data) => camelize(data) as Pool[],
	});
	const { customFields } = useGetCustomFields();
	// Count badge for the Add-ons section. Reuses the section's exact query key (deduped). Ordering is
	// partner-scoped, so this counts the add-ons applicable to this show, in their saved display order.
	const { applicableAddons } = useApplicableAddonsApi();

	const accordion = usePunchupAccordion(ACCORDION_SECTION_IDS, DEFAULT_OPEN_SECTION_IDS);

	const zeroTime = event?.sellingTime === ZERO_TIME;

	// Derived in render (pure timezone conversions of `event`) so the header never paints an
	// "Invalid date" flash for a frame before an Effect commits.
	const firstDateStart = event ? convertUTCToDate(event.days[0]?.startDate, event.timeZone) : '';
	const firstDateEnd = event ? convertUTCToDate(event.days[0]?.endDate, event.timeZone) : '';
	const onSale = event?.sellingTime ?? '';

	// Signal the Punchup parent frame that the dashboard has rendered and event data is ready, exactly
	// once per load. isShowDashboard: true tells the host to render the comedian accordion. A latch keeps
	// a background refetch (which returns a fresh `event` reference) from re-posting (invariant 3).
	const hasPostedReady = useRef(false);
	useEffect(() => {
		if (event && !hasPostedReady.current) {
			hasPostedReady.current = true;
			window.parent.postMessage({ action: 'dashboard-ready', isShowDashboard: true }, '*');
		}
	}, [event]);

	// Notify the host when the dashboard unmounts (e.g. unexpected tixologi redirect).
	// Triggers the host's debounced loading cover so non-displayable pages don't show through.
	useEffect(() => {
		return () => {
			window.parent.postMessage({ action: 'page-leave' }, '*');
		};
	}, []);

	// Fire a toast once when the event API call fails.
	useEffect(() => {
		if (isEventError) {
			postToast('error', t('dashboard-event-load-error', 'Failed to load show data'));
		}
	}, [isEventError, t]);

	const ticketsCount = event?.ticketTypes?.length ?? 0;
	const poolsCount = pools.length;
	const customFieldsCount = customFields?.length ?? 0;
	const addOnsCount = applicableAddons?.length ?? 0;

	// Custom Questions title is mode-agnostic (normal and registration both) — see ./CLAUDE.md.
	const customFieldsTitle = t('dashboard-custom-questions', 'Custom Questions');
	const customFieldsDescription = t(
		'dashboard-custom-questions-description',
		'Information you collect from each guest when they buy or claim a ticket.'
	);

	const isErrorNoData = isEventError && !event;

	return (
		<MainLayout>
			<div className={styles.dashboardWrapper}>
				{/* Header row: stacked title block left, show action buttons right (PUN-4942 req. 9) */}
				{event && (
					<PunchupSplitRow
						align="start"
						stackOnNarrow
						left={
							<EventInfoHeader
								eventTitle={event.name}
								date={moment(firstDateStart).format(HEADER_DATE_FORMAT)}
								startTime={moment(firstDateStart).format(HEADER_TIME_FORMAT)}
								endTime={moment(firstDateEnd).format(HEADER_TIME_FORMAT)}
								address={event.streetAddress}
								onSaleDate={
									zeroTime
										? event.sellingTime
										: `${moment(onSale).format(HEADER_DATE_FORMAT)} · ${moment(onSale).format(HEADER_TIME_FORMAT)}`
								}
								hasPassword={event.has_password}
							/>
						}
						right={<EventActionButtons eventTitle={event.name} />}
					/>
				)}

				{(event || isErrorNoData) && <EventHighlights event={event} isDisabled={isErrorNoData} />}

				{/* Controls row: Expand all / Collapse all only — the actions moved up beside the title */}
				{event && (
					<PunchupAccordionControls
						onExpandAll={accordion.expandAll}
						onCollapseAll={accordion.collapseAll}
						allExpanded={accordion.allExpanded}
						allCollapsed={accordion.allCollapsed}
					/>
				)}

				{(event || isErrorNoData) && (
					<>
						<Prompt when={accordion.hasDirtySections} message="You have unsaved changes. Leave anyway?" />
						{/* `inert` drops the dimmed, dataless subtree from the focus order + a11y tree (pointer-events
					    disables the mouse only); cast because `inert` is absent from this repo's React 17 JSX types. */}
						<div
							className={isErrorNoData ? styles.accordionDisabled : undefined}
							{...(isErrorNoData ? ({ inert: '' } as Record<string, string>) : {})}
						>
							<PunchupAccordionGroup
								openSections={accordion.openSections}
								onToggle={accordion.toggle}
								setDirtySection={accordion.setDirtySection}
							>
								{/* ── Show-focused (how the show appears) ───────────────────── */}
								<PunchupAccordionSection
									id="general"
									title={t('dashboard-general-green', 'Edit Show Details')}
									description={t(
										'dashboard-general-description',
										'Show name, dates, location, access controls, and tags.'
									)}
								>
									<GeneralSection />
								</PunchupAccordionSection>

								<PunchupAccordionSection
									id="image"
									title={t('dashboard-imgContent-accordion-title', 'Show Image')}
									description={t(
										'dashboard-imgContent-accordion-description',
										'The main image shown on your show page.'
									)}
								>
									<ImgContentSection
										eventImgCt={Math.min(1, event?.images?.length ?? 0)}
										eventImageUrl={event?.images?.[0]?.image}
										media={event?.media ?? []}
									/>
								</PunchupAccordionSection>

								{/* ── Ticket-focused (what guests can buy) ──────────────────── */}
								<PunchupAccordionSection
									id="tickets"
									title={t('dashboard-tickets-green', 'Ticket Types')}
									description={t(
										'dashboard-tickets-description',
										'Tiers and prices guests choose from when buying tickets.'
									)}
									count={ticketsCount}
								>
									<TicketTypesSection inAccordion />
								</PunchupAccordionSection>

								{/* Seating Charts and Ticket Pools are mutually exclusive and share this one slot:
						    a seated show caps capacity through its chart, a non-seated one through pools. */}
								{event?.seatsEventKey && (
									<PunchupAccordionSection
										id="seats"
										title={t('dashboard-seats', 'Seating Charts')}
										description={t(
											'dashboard-seats-description',
											'Your seating chart and the comp / promo links that issue tickets for specific sections.'
										)}
									>
										<PunchupSeatsSection event={event} />
									</PunchupAccordionSection>
								)}

								{!event?.seatsEventKey && (
									<PunchupAccordionSection
										id="pools"
										title={t('dashboard-pools-green', 'Ticket Pools')}
										description={t(
											'dashboard-pools-description',
											'One shared capacity that several ticket tiers draw from.'
										)}
										count={poolsCount}
									>
										<PoolsSection inAccordion />
									</PunchupAccordionSection>
								)}

								<PunchupAccordionSection
									id="addons"
									title={t('dashboard-addOns', 'Add Ons')}
									description={t(
										'dashboard-addOns-description',
										'Extras guests can add on when buying a ticket. The order set here applies to every show.'
									)}
									count={addOnsCount}
								>
									<AddOnsSection />
								</PunchupAccordionSection>

								{/* ── Per-guest data (what we ask attendees) ────────────────── */}
								<PunchupAccordionSection
									id="customfields"
									title={customFieldsTitle}
									description={customFieldsDescription}
									count={customFieldsCount}
								>
									<CustomFieldsSection eventMode={event?.mode ?? 'normal'} />
								</PunchupAccordionSection>
							</PunchupAccordionGroup>
						</div>
					</>
				)}
			</div>
		</MainLayout>
	);
};

export { PunchupDashboard };
